import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { extractErrorMessage } from './errorMessage';
import QuestionsStep from './QuestionsStep';
import AvailabilityPicker, {
  expandSlots,
  pickDefaultUsTimezone,
} from './AvailabilityPicker';
import './InterviewRequestForm.css';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function prefersReducedMotion() {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch (e) {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * InterviewRequestForm — the two-step swipe-right interview wizard shell.
 *
 * Step 1 (QuestionsStep): seven suggested-question chips + a custom input.
 * Step 2 (AvailabilityPicker): timezone, day strip, slot grid, "Your picks",
 *   plus the footer that offers the interview path and the connect-only path.
 *
 * The shell owns: the two-segment progress bar, the pinned listing-context
 * header, the close (X), step state, the horizontal slide transition (which
 * degrades to a fade under prefers-reduced-motion), and bottom-sheet styling
 * on mobile vs centered modal on desktop. It renders through a portal into
 * <body> so it escapes any parent stacking context.
 *
 * onSubmit receives { mode, slots, questions }:
 *   - mode 'interview': slots is the expanded DTO array; an interview request.
 *   - mode 'connect': slots is []; the "Not right now, just connect us" path.
 *   questions is always a plain string[] (may be empty).
 * onCancel is the X / backdrop / Esc dismiss: it creates nothing.
 */
export default function InterviewRequestForm({
  isOpen,
  listingTitle,
  facilityName,
  providerRole,
  rateLabel,
  locationLabel,
  onSubmit,
  onCancel,
  defaultTimezone,
  requireIntroConsent,
}) {
  const initialTimezone = defaultTimezone || pickDefaultUsTimezone();

  const [step, setStep] = useState(1); // 1 = questions, 2 = availability
  const [questions, setQuestions] = useState([]); // [{ id?, text }]
  const [slots, setSlots] = useState([]); // [{ dateKey, startTime }]
  const [timezone, setTimezone] = useState(initialTimezone);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState(null); // which button is busy
  const [error, setError] = useState(null);

  const reducedMotion = prefersReducedMotion();

  // Reset state when the modal closes and re-opens so a second request starts
  // fresh — stale questions/slots from a cancelled run must not leak across.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setStep(1);
      setQuestions([]);
      setSlots([]);
      setTimezone(initialTimezone);
      setSubmitting(false);
      setSubmitMode(null);
      setError(null);
    }
    wasOpenRef.current = isOpen;
    // initialTimezone is derived; intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Body scroll lock + Esc to cancel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submitting]);

  const questionTexts = useMemo(
    () => questions.map((q) => q.text),
    [questions],
  );

  const canSendInterview = slots.length >= 1;

  /* ----- Handlers ----- */

  const handleCancel = useCallback(() => {
    if (submitting) return;
    onCancel();
  }, [submitting, onCancel]);

  const goToStep2 = useCallback(() => {
    setError(null);
    setStep(2);
  }, []);

  const goToStep1 = useCallback(() => {
    setError(null);
    setStep(1);
  }, []);

  const runSubmit = useCallback(
    async (mode) => {
      if (submitting) return;
      if (mode === 'interview' && !canSendInterview) return;
      setSubmitting(true);
      setSubmitMode(mode);
      setError(null);
      try {
        await onSubmit({
          mode,
          slots: mode === 'interview' ? expandSlots(slots, timezone) : [],
          questions: questionTexts,
          // External listings: tapping "Send request & introduce me" IS the
          // consent to a direct introduction. The provider sees exactly what is
          // shared in the consent block above the button before they tap.
          ...(requireIntroConsent && mode === 'interview'
            ? { introConsent: true }
            : {}),
        });
      } catch (err) {
        setError(
          extractErrorMessage(
            err,
            mode === 'interview'
              ? 'Could not send the interview request. Try again.'
              : 'Could not connect you with this group. Try again.',
          ),
        );
        setSubmitting(false);
        setSubmitMode(null);
      }
    },
    [
      submitting,
      canSendInterview,
      onSubmit,
      slots,
      timezone,
      questionTexts,
      requireIntroConsent,
    ],
  );

  if (!isOpen) return null;

  /* ----- Render ----- */

  const contextLineParts = [providerRole, listingTitle, rateLabel].filter(
    Boolean,
  );
  const contextSubParts = [facilityName, locationLabel].filter(Boolean);

  const trackClass = [
    'ir-step-track',
    `ir-step-${step}`,
    reducedMotion ? 'ir-no-slide' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const modal = (
    <div
      className="ir-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ir-step-title"
      onMouseDown={(e) => {
        // Backdrop click cancels — mousedown only so a drag from inside the
        // sheet does not accidentally dismiss it.
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className="ir-sheet interview-request-form"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ir-grip" aria-hidden="true" />

        {/* Two-segment progress */}
        <div className="ir-prog">
          <span
            data-testid="ir-progress-segment"
            className="ir-seg fill"
          />
          <span
            data-testid="ir-progress-segment"
            className={`ir-seg${step >= 2 ? ' fill' : ''}`}
          />
        </div>

        {/* Pinned listing-context header */}
        <header className="ir-shead">
          {step === 2 && (
            <button
              type="button"
              className="ir-navbtn"
              onClick={goToStep1}
              aria-label="Back"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden="true">
                <path
                  d="M8 2 L4 6.5 L8 11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <div className="ir-ctx">
            {contextLineParts.length > 0 && (
              <div className="ir-ctx-title">
                {contextLineParts.join(' · ')}
              </div>
            )}
            {contextSubParts.length > 0 && (
              <div className="ir-ctx-sub">{contextSubParts.join(' · ')}</div>
            )}
          </div>
          <button
            type="button"
            className="ir-navbtn"
            onClick={handleCancel}
            aria-label="Close"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M1 1 L11 11 M11 1 L1 11"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* Sliding step track */}
        <div className="ir-step-viewport">
          <div className={trackClass} data-testid="ir-step-track">
            {/* Step 1 — Questions */}
            <section className="ir-step-panel" aria-hidden={step !== 1}>
              <div className="ir-sbody">
                <div className="ir-eyebrow">Step 1 of 2</div>
                <h2 id={step === 1 ? 'ir-step-title' : undefined} className="ir-s-title">
                  Anything you want to ask {facilityName || 'this group'}?
                </h2>
                <p className="ir-s-sub">
                  Optional. Your questions reach the group the moment you
                  connect.
                </p>
                <QuestionsStep
                  questions={questions}
                  onQuestionsChange={setQuestions}
                />
              </div>
              <div className="ir-sfoot">
                <button
                  type="button"
                  className="ir-cta"
                  onClick={goToStep2}
                >
                  Continue
                </button>
                <div className="ir-fhint">
                  {questions.length > 0 ? (
                    <>
                      <strong>
                        {questions.length} question
                        {questions.length === 1 ? '' : 's'} ready
                      </strong>
                      {' · or continue, it is optional'}
                    </>
                  ) : (
                    'Continue with none, it is optional'
                  )}
                </div>
              </div>
            </section>

            {/* Step 2 — Interview and availability */}
            <section className="ir-step-panel" aria-hidden={step !== 2}>
              <div className="ir-sbody">
                <div className="ir-eyebrow">Step 2 of 2</div>
                <h2 id={step === 2 ? 'ir-step-title' : undefined} className="ir-s-title">
                  Want to interview with {facilityName || 'this group'}?
                </h2>
                <p className="ir-s-sub">
                  Pick every time you are open and we send the request
                  {questions.length > 0
                    ? ` with your ${questions.length} question${
                        questions.length === 1 ? '' : 's'
                      }`
                    : ''}
                  . Each interview runs 30 minutes.
                </p>
                <AvailabilityPicker
                  slots={slots}
                  onSlotsChange={setSlots}
                  timezone={timezone}
                  onTimezoneChange={setTimezone}
                />
              </div>
              <div className="ir-sfoot">
                {error && (
                  <div className="ir-error" role="alert">
                    {error}
                  </div>
                )}
                {requireIntroConsent ? (
                  /* External listing: the introduction is the only path. The
                     button is the consent; there is no masked "just connect"
                     option, because a cold poster has no in-app inbox to act
                     on a masked ping. Closing the sheet is the way out. */
                  <>
                    <div className="ir-consent">
                      <p className="ir-consent-title">
                        We&apos;ll introduce you directly
                      </p>
                      <p className="ir-consent-copy">
                        To set up the interview, we share your name, email, and
                        phone with the recruiter for{' '}
                        {facilityName || 'this listing'} so they can reach you.
                        RxRooster just makes the connection. Nothing is shared
                        until you send.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ir-cta"
                      onClick={() => runSubmit('interview')}
                      disabled={!canSendInterview || submitting}
                    >
                      {submitting && submitMode === 'interview' ? (
                        <span className="ir-spinner" aria-hidden="true" />
                      ) : (
                        'Send request & introduce me'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ir-cta"
                      onClick={() => runSubmit('interview')}
                      disabled={!canSendInterview || submitting}
                    >
                      {submitting && submitMode === 'interview' ? (
                        <span className="ir-spinner" aria-hidden="true" />
                      ) : (
                        'Send interview request'
                      )}
                    </button>
                    <button
                      type="button"
                      className="ir-cta2"
                      onClick={() => runSubmit('connect')}
                      disabled={submitting}
                    >
                      {submitting && submitMode === 'connect' ? (
                        <span
                          className="ir-spinner ir-spinner-dark"
                          aria-hidden="true"
                        />
                      ) : (
                        'Not right now, just connect us'
                      )}
                    </button>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal into <body> so the modal escapes any parent stacking context (the
  // swipe deck's control bar sits at a high z-index and was bleeding through).
  return createPortal(modal, document.body);
}

InterviewRequestForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  listingTitle: PropTypes.string,
  facilityName: PropTypes.string,
  providerRole: PropTypes.string,
  rateLabel: PropTypes.string,
  locationLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  defaultTimezone: PropTypes.string,
  requireIntroConsent: PropTypes.bool,
};

InterviewRequestForm.defaultProps = {
  listingTitle: null,
  facilityName: null,
  providerRole: null,
  rateLabel: null,
  locationLabel: null,
  defaultTimezone: null,
  requireIntroConsent: false,
};
